import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import EmployeeKycWizardModal from '../../components/kyc/EmployeeKycWizardModal'
import {
  UserCircleIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  CalendarIcon,
  IdentificationIcon,
  FlagIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline'

const STAFF_DOC_TYPES = [
  { value: 'self_declaration', label: 'Self-declaration' },
  { value: 'pep_declaration', label: 'PEP / conflict declaration' },
  { value: 'kyc', label: 'KYC / identity' },
  { value: 'certification', label: 'Certification / training record' },
  { value: 'general', label: 'General' },
]

function formatStaffDocType(t) {
  const row = STAFF_DOC_TYPES.find((x) => x.value === t)
  return row ? row.label : (t || 'General').replace(/_/g, ' ')
}

export default function EmployeeDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const fileRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [employee, setEmployee] = useState(null)
  const [kycStatus, setKycStatus] = useState(null)
  const [amlStatus, setAmlStatus] = useState(null)
  const [activeTab, setActiveTab] = useState('overview') // overview, kyc, aml, documents
  const [staffDocuments, setStaffDocuments] = useState([])
  const [staffDocsLoading, setStaffDocsLoading] = useState(false)
  const [docUploadType, setDocUploadType] = useState('self_declaration')
  const [docError, setDocError] = useState(null)
  const [docUploading, setDocUploading] = useState(false)
  const [kycWizardOpen, setKycWizardOpen] = useState(false)
  const [deferOpen, setDeferOpen] = useState(false)
  const [deferReason, setDeferReason] = useState('')
  const [deferLoading, setDeferLoading] = useState(false)
  const [actionError, setActionError] = useState(null)

  const loadStaffDocuments = useCallback(async () => {
    if (!id) return
    setStaffDocsLoading(true)
    setDocError(null)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/staff/${id}/documents`))
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to load staff documents')
      setStaffDocuments(Array.isArray(j.data) ? j.data : [])
    } catch (e) {
      setStaffDocuments([])
      setDocError(e.message || 'Failed to load documents')
    } finally {
      setStaffDocsLoading(false)
    }
  }, [id])

  const loadEmployeeDetails = useCallback(async () => {
    setLoading(true)
    setDocError(null)
    try {
      const empResponse = await authFetch(getLocalApiUrl(`/api/staff/${id}`))
      const empData = await empResponse.json()

      if (empData.success) {
        setEmployee(empData.data)

        const [kycResponse, amlResponse] = await Promise.all([
          authFetch(getLocalApiUrl(`/api/staff/${id}/kyc-status`)),
          authFetch(getLocalApiUrl(`/api/staff/${id}/aml-status`)),
        ])
        const kycData = await kycResponse.json()
        const amlData = await amlResponse.json()
        if (kycData.success) setKycStatus(kycData.data)
        if (amlData.success) setAmlStatus(amlData.data)

        await loadStaffDocuments()
      } else {
        setEmployee(null)
        setKycStatus(null)
        setAmlStatus(null)
        setStaffDocuments([])
      }
    } catch (error) {
      console.error('Error loading employee details:', error)
      setEmployee(null)
    } finally {
      setLoading(false)
    }
  }, [id, loadStaffDocuments])

  useEffect(() => {
    loadEmployeeDetails()
  }, [loadEmployeeDetails])

  const openStaffDocument = async (docId, fileName) => {
    try {
      setDocError(null)
      const url = getLocalApiUrl(`/api/staff/${id}/documents/${encodeURIComponent(String(docId))}/download`)
      const res = await authFetch(url)
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `Download failed (${res.status})`)
      }
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const w = window.open(objectUrl, '_blank', 'noopener,noreferrer')
      if (!w) {
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = fileName || 'document'
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 8000)
    } catch (e) {
      setDocError(e.message || 'Could not open document')
    }
  }

  const uploadStaffDocument = async () => {
    const input = fileRef.current
    const file = input?.files?.[0]
    if (!file) {
      setDocError('Choose a file to upload')
      return
    }
    setDocUploading(true)
    setDocError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const qs = new URLSearchParams({ type: docUploadType })
      const r = await authFetch(getLocalApiUrl(`/api/staff/${id}/documents?${qs}`), { method: 'POST', body: fd })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || `Upload failed (${r.status})`)
      if (input) input.value = ''
      await loadStaffDocuments()
    } catch (e) {
      setDocError(e.message || 'Upload failed')
    } finally {
      setDocUploading(false)
    }
  }

  const openKycWizard = () => {
    setActionError(null)
    setKycWizardOpen(true)
  }

  const handleKycDefer = async () => {
    setDeferLoading(true)
    setActionError(null)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/staff/${id}/kyc-defer`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: deferReason.trim() || undefined }),
      })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'Could not defer KYC')
      setDeferOpen(false)
      setDeferReason('')
      await loadEmployeeDetails()
    } catch (e) {
      setActionError(e.message || 'Defer failed')
    } finally {
      setDeferLoading(false)
    }
  }

  const kycNeedsAction = () => {
    const s = kycStatus?.status
    return !s || s === 'NOT_STARTED' || s === 'NOT_SCREENED' || s === 'DEFERRED' || s === 'IN_PROGRESS'
  }

  const kycActionLabel = () => {
    const s = kycStatus?.status
    if (s === 'IN_PROGRESS') return 'Continue KYC wizard'
    if (s === 'DEFERRED') return 'Complete KYC wizard'
    if (s === 'FAILED' || s === 'REQUIRES_REVIEW') return 'Retry KYC wizard'
    return 'Start KYC wizard'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-psg-teal mx-auto mb-4"></div>
          <p className="text-gray-600">Loading employee details...</p>
        </div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Employee Not Found</h3>
          <p className="text-red-700 mb-4">The requested employee could not be found.</p>
          <button
            onClick={() => navigate('/admin/employees')}
            className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700"
          >
            Back to Employees
          </button>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status) => {
    const badges = {
      'VERIFIED': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: CheckCircleIcon },
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: ClockIcon },
      'IN_PROGRESS': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: ClockIcon },
      'NOT_STARTED': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: ClockIcon },
      'DEFERRED': { bg: 'bg-amber-100', text: 'text-amber-900', border: 'border-amber-200', icon: ClockIcon },
      'NOT_SCREENED': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: ClockIcon },
      'REQUIRES_REVIEW': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', icon: ExclamationTriangleIcon },
      'FAILED': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', icon: ExclamationTriangleIcon },
      'MATCH_FOUND': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', icon: FlagIcon },
      'CLEAN': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: CheckCircleIcon }
    }
    const badge = badges[status] || badges['PENDING']
    const Icon = badge.icon
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.bg} ${badge.text} ${badge.border}`}>
        <Icon className="w-4 h-4 mr-1.5" />
        {status?.replace(/_/g, ' ')}
      </span>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/employees')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{employee.name}</h1>
            <p className="text-sm text-gray-500">Employee ID: {employee.employee_id || 'Not assigned'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {employee.active ? (
            <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
              Active
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-medium">
              Inactive
            </span>
          )}
        </div>
      </div>

      {actionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      )}

      {kycNeedsAction() && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-amber-950">Identity verification required</h3>
            <p className="text-sm text-amber-900 mt-1">
              {kycStatus?.status === 'DEFERRED'
                ? 'KYC was deferred — complete the wizard when the employee is ready.'
                : kycStatus?.status === 'IN_PROGRESS'
                  ? 'KYC onboarding is in progress — continue where you left off.'
                  : 'Upload ID, run ProfileSafe OCR, and submit for DVS / AML screening.'}
            </p>
            {kycStatus?.status === 'DEFERRED' && kycStatus.defer_reason && (
              <p className="text-xs text-amber-800 mt-2">
                Reason: {kycStatus.defer_reason}
                {kycStatus.deferred_at
                  ? ` · ${new Date(kycStatus.deferred_at).toLocaleDateString()}`
                  : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={openKycWizard}
              className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700 text-sm font-medium"
            >
              {kycActionLabel()}
            </button>
            {(kycStatus?.status === 'NOT_STARTED' ||
              kycStatus?.status === 'NOT_SCREENED' ||
              !kycStatus?.status) && (
              <button
                type="button"
                onClick={() => setDeferOpen(true)}
                className="px-4 py-2 border border-amber-700 text-amber-950 rounded-lg hover:bg-amber-100 text-sm font-medium"
              >
                Skip for now
              </button>
            )}
          </div>
        </div>
      )}

      {/* Status Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Basic Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Basic Information</h3>
            <UserCircleIcon className="w-6 h-6 text-gray-400" />
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-gray-500">Role</div>
              <div className="font-medium text-gray-900">{employee.role}</div>
            </div>
            <div>
              <div className="text-gray-500">Scope</div>
              <div className="font-medium text-gray-900 capitalize">{employee.level}</div>
            </div>
            {employee.venue_id && (
              <div>
                <div className="text-gray-500">Venue</div>
                <div className="font-medium text-gray-900">{employee.venue_id}</div>
              </div>
            )}
          </div>
        </div>

        {/* KYC Status Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">KYC Status</h3>
            <IdentificationIcon className="w-6 h-6 text-gray-400" />
          </div>
          {kycStatus ? (
            <div className="space-y-3">
              <div>{getStatusBadge(kycStatus.status)}</div>
              {kycStatus.verified_date && (
                <div className="text-sm">
                  <div className="text-gray-500">Verified Date</div>
                  <div className="font-medium text-gray-900">
                    {new Date(kycStatus.verified_date).toLocaleDateString()}
                  </div>
                </div>
              )}
              {kycStatus.documents_count !== undefined && (
                <div className="text-sm">
                  <div className="text-gray-500">KYC registry files</div>
                  <div className="font-medium text-gray-900">{kycStatus.documents_count} uploaded</div>
                </div>
              )}
              <div className="text-sm">
                <div className="text-gray-500">Staff uploads</div>
                <div className="font-medium text-gray-900">
                  {staffDocsLoading ? '…' : `${staffDocuments.length} on file`}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">No KYC data available</div>
          )}
        </div>

        {/* AML/PEP Status Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">AML/PEP Screening</h3>
            <ShieldCheckIcon className="w-6 h-6 text-gray-400" />
          </div>
          {amlStatus ? (
            <div className="space-y-3">
              <div>{getStatusBadge(amlStatus.status)}</div>
              {amlStatus.last_screening_date && (
                <div className="text-sm">
                  <div className="text-gray-500">Last Screened</div>
                  <div className="font-medium text-gray-900">
                    {new Date(amlStatus.last_screening_date).toLocaleDateString()}
                  </div>
                </div>
              )}
              {amlStatus.matches_count !== undefined && amlStatus.matches_count > 0 && (
                <div className="text-sm">
                  <div className="text-gray-500">Matches Found</div>
                  <div className="font-medium text-red-600">{amlStatus.matches_count} potential matches</div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Not screened</div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('kyc')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'kyc'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              KYC Details
            </button>
            <button
              onClick={() => setActiveTab('aml')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'aml'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              AML/PEP Screening
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('documents')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
          </nav>
        </div>

        <div className="p-6">
          {docError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {docError}
            </div>
          )}
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Start Date</div>
                    <div className="flex items-center text-gray-900">
                      <CalendarIcon className="w-5 h-5 mr-2 text-gray-400" />
                      {employee.start_date ? new Date(employee.start_date).toLocaleDateString() : 'Not set'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">End Date</div>
                    <div className="flex items-center text-gray-900">
                      <CalendarIcon className="w-5 h-5 mr-2 text-gray-400" />
                      {employee.end_date ? new Date(employee.end_date).toLocaleDateString() : 'Current'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Created</div>
                    <div className="text-gray-900">
                      {new Date(employee.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Last Updated</div>
                    <div className="text-gray-900">
                      {new Date(employee.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {kycStatus && kycStatus.status !== 'NOT_SCREENED' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">KYC Verification</span>
                      {getStatusBadge(kycStatus.status)}
                    </div>
                    {amlStatus && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">AML/PEP Screening</span>
                        {getStatusBadge(amlStatus.status)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* KYC Tab */}
          {activeTab === 'kyc' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">KYC Verification Details</h3>
                {kycStatus ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2 justify-end">
                      <button
                        type="button"
                        onClick={openKycWizard}
                        className="px-3 py-1.5 text-sm border border-psg-teal text-psg-teal rounded-lg hover:bg-teal-50"
                      >
                        {kycActionLabel()}
                      </button>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Status</div>
                          <div>{getStatusBadge(kycStatus.status)}</div>
                        </div>
                        {kycStatus.verified_date && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Verification Date</div>
                            <div className="text-gray-900">
                              {new Date(kycStatus.verified_date).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                        {kycStatus.verified_by && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Verified By</div>
                            <div className="text-gray-900">{kycStatus.verified_by}</div>
                          </div>
                        )}
                        {kycStatus.documents_count !== undefined && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Documents Uploaded</div>
                            <div className="text-gray-900">{kycStatus.documents_count} documents</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {kycStatus.documents && kycStatus.documents.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Uploaded Documents</h4>
                        <div className="space-y-2">
                          {kycStatus.documents.map((doc, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                              <div className="flex items-center">
                                <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{doc.document_type}</div>
                                  <div className="text-xs text-gray-500">
                                    Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <button className="text-sm text-psg-teal hover:text-psg-teal-700">
                                View
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {kycStatus.notes && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Notes</h4>
                        <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                          {kycStatus.notes}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <IdentificationIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No KYC verification records found</p>
                    <button
                      type="button"
                      onClick={openKycWizard}
                      className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700"
                    >
                      Start KYC wizard
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AML/PEP Tab */}
          {activeTab === 'aml' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AML/PEP Screening Details</h3>
                {amlStatus ? (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Screening Status</div>
                          <div>{getStatusBadge(amlStatus.status)}</div>
                        </div>
                        {amlStatus.last_screening_date && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Last Screened</div>
                            <div className="text-gray-900">
                              {new Date(amlStatus.last_screening_date).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                        {amlStatus.screened_by && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Screened By</div>
                            <div className="text-gray-900">{amlStatus.screened_by}</div>
                          </div>
                        )}
                        {amlStatus.matches_count !== undefined && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Matches Found</div>
                            <div className={`font-medium ${amlStatus.matches_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {amlStatus.matches_count} {amlStatus.matches_count === 1 ? 'match' : 'matches'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {amlStatus.matches && amlStatus.matches.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                          Potential AML/PEP Matches
                        </h4>
                        <div className="space-y-3">
                          {amlStatus.matches.map((match, idx) => (
                            <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <div className="font-medium text-gray-900">{match.matched_name}</div>
                                  <div className="text-sm text-gray-600">{match.match_type}</div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  match.confidence_score >= 80 ? 'bg-red-100 text-red-800' :
                                  match.confidence_score >= 60 ? 'bg-orange-100 text-orange-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {match.confidence_score}% match
                                </span>
                              </div>
                              {match.risk_categories && match.risk_categories.length > 0 && (
                                <div className="mb-2">
                                  <div className="text-sm text-gray-700 mb-1">Risk Categories:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {match.risk_categories.map((cat, i) => (
                                      <span key={i} className="px-2 py-1 bg-white border border-red-300 rounded text-xs text-red-700">
                                        {cat}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {match.notes && (
                                <div className="text-sm text-gray-700 mt-2">
                                  <strong>Notes:</strong> {match.notes}
                                </div>
                              )}
                              <div className="mt-3 flex space-x-2">
                                <button className="text-sm text-psg-teal hover:text-psg-teal-700 font-medium">
                                  Review Match
                                </button>
                                <button className="text-sm text-gray-600 hover:text-gray-800">
                                  Mark as False Positive
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {amlStatus.screening_history && amlStatus.screening_history.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Screening History</h4>
                        <div className="space-y-2">
                          {amlStatus.screening_history.map((screening, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                              <div className="flex items-center">
                                <ClockIcon className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm text-gray-900">
                                    {new Date(screening.screening_date).toLocaleDateString()}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {screening.matches_found ? `${screening.matches_found} matches found` : 'No matches'}
                                  </div>
                                </div>
                              </div>
                              {getStatusBadge(screening.result)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <ShieldCheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No AML/PEP screening records found</p>
                    <button className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700">
                      Run AML/PEP Screening
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Staff documents</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Upload self-declarations, PEP forms, certifications, or other HR/compliance files. Stored separately from patron KYC registry.
                </p>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="staff-doc-type" className="block text-xs font-medium text-gray-600 mb-1">
                        Document type
                      </label>
                      <select
                        id="staff-doc-type"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-psg-teal focus:outline-none focus:ring-1 focus:ring-psg-teal"
                        value={docUploadType}
                        onChange={(e) => setDocUploadType(e.target.value)}
                      >
                        {STAFF_DOC_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="staff-doc-file" className="block text-xs font-medium text-gray-600 mb-1">
                        File
                      </label>
                      <input
                        id="staff-doc-file"
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                        className="block w-full text-sm text-gray-600 file:mr-3 file:rounded file:border-0 file:bg-psg-teal file:px-3 file:py-1.5 file:text-white file:hover:bg-psg-teal-700"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={uploadStaffDocument}
                      disabled={docUploading}
                      className="inline-flex items-center gap-2 rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white hover:bg-psg-teal-700 disabled:opacity-50"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      {docUploading ? 'Uploading…' : 'Upload'}
                    </button>
                    <span className="text-xs text-gray-500">
                      Requires <span className="font-mono">admin/user-management:manage</span>. PDF or images up to your server limit.
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">On file</h4>
                {staffDocsLoading ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : staffDocuments.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    No documents uploaded yet.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                    {staffDocuments.map((doc) => (
                      <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                        <div className="flex min-w-0 items-center gap-3">
                          <DocumentTextIcon className="h-5 w-5 shrink-0 text-gray-400" />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate" title={doc.file_name}>
                              {doc.file_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatStaffDocType(doc.document_type)}
                              {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleString()}` : ''}
                              {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}
                              {doc.size_bytes != null ? ` · ${(Number(doc.size_bytes) / 1024).toFixed(1)} KB` : ''}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => openStaffDocument(doc.id, doc.file_name)}
                          className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-psg-teal hover:bg-gray-50"
                        >
                          Open
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <EmployeeKycWizardModal
        open={kycWizardOpen}
        onClose={() => setKycWizardOpen(false)}
        staffId={id}
        employeeName={employee.name}
        employeeId={employee.employee_id || ''}
        onComplete={() => {
          setKycWizardOpen(false)
          loadEmployeeDetails()
        }}
      />

      {deferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Skip KYC for now?</h3>
            <p className="text-sm text-gray-600">
              The employee record stays active. You can complete identity verification later from this page.
            </p>
            <label className="block text-sm">
              <span className="text-gray-700">Reason (optional)</span>
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                rows={3}
                maxLength={500}
                value={deferReason}
                onChange={(e) => setDeferReason(e.target.value.slice(0, 500))}
                placeholder="e.g. starts next week; will verify on first shift"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                onClick={() => setDeferOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deferLoading}
                className="px-4 py-2 text-sm rounded-lg bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50"
                onClick={handleKycDefer}
              >
                {deferLoading ? 'Saving…' : 'Skip for now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
